import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiStar, FiZap, FiCreditCard, FiCalendar, FiMusic, FiAward, FiHeart, FiSmile, FiThumbsUp, FiCoffee } = FiIcons;

const SupportModal = ({ onClose }) => {
  const { t, language } = useLanguage();
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState('one-time');
  const isChinese = language === 'zh-TW';
  
  const getPlanTitle = (amount, type) => {
    if (type === 'subscription') {
      if (amount === 5) return t('supportModal.planNames.basic');
      if (amount === 8) return t('supportModal.planNames.pro');
      if (amount === 10) return t('supportModal.planNames.premium');
    } else {
      if (amount === 5) return t('supportModal.planNames.basic');
      if (amount === 8) return t('supportModal.planNames.pro');
      if (amount === 10) return t('supportModal.planNames.premium');
    }
    return 'Plan';
  };
  
  // Enhanced descriptions for each tier
  const getFeatures = (amount, type) => {
    const features = [];
    
    // Thank you message based on tier
    if (amount === 5) {
      features.push(t('supportModal.planFeatures.thanksMessage.basic'));
      // Simple tier description
      features.push(isChinese ? '讓我們繼續為您提供免費工具' : 'Help us keep providing free tools');
      features.push(isChinese ? '您的支持使我們能夠維護服務' : 'Your support helps maintain our service');
    } else if (amount === 8) {
      features.push(t('supportModal.planFeatures.thanksMessage.pro'));
      // Mid-tier description
      features.push(isChinese ? '幫助我們創建更多音樂工具' : 'Help us create more music tools');
      features.push(isChinese ? '您的支持對我們意義重大' : 'Your contribution means a lot to us');
    } else if (amount === 10) {
      features.push(t('supportModal.planFeatures.thanksMessage.premium'));
      // Premium tier description
      features.push(isChinese ? '成為我們的頂級贊助者' : 'Become our premium supporter');
      features.push(isChinese ? '您的慷慨讓更多創新成為可能' : 'Your generosity makes more innovation possible');
    }
    
    return features;
  };
  
  const pricingPlans = [
    {
      amount: 5,
      priceId: "price_1RvjDaIEnneB4h0mJEC7ISg6",
      paymentLink: "https://buy.stripe.com/00w9AU4iDgxnb5RcKS0Fi0q",
      currency: "usd",
      type: "one-time",
      popular: false,
      icon: FiCoffee,
      features: getFeatures(5, 'one-time')
    },
    {
      amount: 8,
      priceId: "price_1RvjDaIEnneB4h0mk0cTX5Ew",
      paymentLink: "https://buy.stripe.com/8x24gAeXh80RgqbdOW0Fi0n",
      currency: "usd",
      type: "one-time",
      popular: true,
      icon: FiThumbsUp,
      features: getFeatures(8, 'one-time')
    },
    {
      amount: 10,
      priceId: "price_1RvjDaIEnneB4h0mdXJqGYRQ",
      paymentLink: "https://buy.stripe.com/4gMeVeg1la8Z0rd9yG0Fi0p",
      currency: "usd",
      type: "one-time",
      popular: false,
      icon: FiHeart,
      features: getFeatures(10, 'one-time')
    },
    {
      amount: 5,
      priceId: "price_1RvjDaIEnneB4h0mosOYps1w",
      paymentLink: "https://buy.stripe.com/00w00k3ez80Rfm7aCK0Fi0m",
      currency: "usd",
      interval: "month",
      type: "subscription",
      popular: false,
      icon: FiCoffee,
      features: getFeatures(5, 'subscription')
    },
    {
      amount: 8,
      priceId: "price_1RvjDaIEnneB4h0mVhKnBZhS",
      paymentLink: "https://buy.stripe.com/eVq3cwdTd3KBei37qy0Fi0r",
      currency: "usd",
      interval: "month",
      type: "subscription",
      popular: true,
      icon: FiThumbsUp,
      features: getFeatures(8, 'subscription')
    },
    {
      amount: 10,
      priceId: "price_1RvjDaIEnneB4h0m483j9NX6",
      paymentLink: "https://buy.stripe.com/4gM14o7uPbd3c9V9yG0Fi0o",
      currency: "usd",
      interval: "month",
      type: "subscription",
      popular: false,
      icon: FiHeart,
      features: getFeatures(10, 'subscription')
    }
  ];

  const handlePurchase = (paymentLink) => {
    window.open(paymentLink, '_blank');
    onClose();
  };

  const formatPrice = (amount, currency, interval) => {
    const symbol = currency === 'usd' ? '$' : currency.toUpperCase();
    const price = `${symbol}${amount}`;
    return interval ? `${price}/${interval}` : price;
  };

  const oneTimePlans = pricingPlans.filter(plan => plan.type === 'one-time');
  const subscriptionPlans = pricingPlans.filter(plan => plan.type === 'subscription');
  
  const modalText = {
    title: t('supportModal.title'),
    subtitle: t('supportModal.subtitle'),
    oneTime: t('supportModal.oneTime'),
    monthly: t('supportModal.monthly'),
    mostPopular: t('supportModal.mostPopular'),
    bestValue: t('supportModal.bestValue'),
    oneTimePayment: t('supportModal.oneTimePayment'),
    recurringMonthly: t('supportModal.recurringMonthly'),
    choosePlan: t('supportModal.choosePlan'),
    subscribeNow: t('supportModal.subscribeNow'),
    securePayment: t('supportModal.securePayment')
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Prevent body scrolling when modal is open with improved scrollbar compensation
  useEffect(() => {
    // Store original body and html styles before any modification
    const originalBodyStyles = {
      overflow: document.body.style.overflow || '',
      paddingRight: document.body.style.paddingRight || '',
    };
    
    const originalHtmlStyles = {
      overflow: document.documentElement.style.overflow || '',
      marginRight: document.documentElement.style.marginRight || '',
    };
    
    // Calculate scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Only apply padding adjustments if there's actually a scrollbar
    if (scrollbarWidth > 0) {
      // Method 1: Lock the body without shifting content
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Method 2: Apply the same adjustment to the html element to maintain alignment
      document.documentElement.style.overflow = 'hidden';
      // Don't add marginRight to html as it can cause other issues
    } else {
      // If no scrollbar, just prevent scrolling
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    
    // Create a marker div to measure any potential shifts
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'fixed';
    markerDiv.style.top = '0';
    markerDiv.style.right = '0';
    markerDiv.style.width = '1px';
    markerDiv.style.height = '100vh';
    markerDiv.style.pointerEvents = 'none';
    markerDiv.style.zIndex = '-1';
    markerDiv.dataset.modalMarker = 'true';
    document.body.appendChild(markerDiv);
    
    // Initial position
    const initialPosition = markerDiv.getBoundingClientRect().right;
    
    return () => {
      // Check if there was any shift
      const finalPosition = markerDiv.getBoundingClientRect().right;
      const shift = finalPosition - initialPosition;
      
      // Remove the marker element
      document.body.removeChild(markerDiv);
      
      // First restore original styles
      document.body.style.overflow = originalBodyStyles.overflow;
      document.body.style.paddingRight = originalBodyStyles.paddingRight;
      document.documentElement.style.overflow = originalHtmlStyles.overflow;
      document.documentElement.style.marginRight = originalHtmlStyles.marginRight;
      
      // If there was a shift, apply a temporary counter-adjustment to smooth the transition
      if (Math.abs(shift) > 0.5) {
        // Apply a temporary fix to counter any shift during the transition
        const tempFix = document.createElement('style');
        tempFix.innerHTML = `
          body {
            transition: padding-right 0.01s ease-out;
          }
        `;
        document.head.appendChild(tempFix);
        
        // Remove the temporary style after the transition completes
        setTimeout(() => {
          if (document.head.contains(tempFix)) {
            document.head.removeChild(tempFix);
          }
        }, 100);
      }
    };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Modal Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{modalText.title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-5">
          <p className="text-center text-gray-600 dark:text-gray-300 mb-8">
            {modalText.subtitle}
          </p>
          
          <div className="space-y-8">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button 
                onClick={() => setActiveTab('one-time')}
                className={`w-1/2 text-center pb-2 ${
                  activeTab === 'one-time' 
                    ? 'border-b-2 border-primary-500' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className={`flex items-center justify-center font-medium ${
                  activeTab === 'one-time'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  <SafeIcon icon={FiCreditCard} className="w-5 h-5 mr-2" />
                  <span>{modalText.oneTime}</span>
                </div>
              </button>
              
              <button 
                onClick={() => setActiveTab('subscription')}
                className={`w-1/2 text-center pb-2 ${
                  activeTab === 'subscription' 
                    ? 'border-b-2 border-primary-500' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className={`flex items-center justify-center font-medium ${
                  activeTab === 'subscription'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                  <span>{modalText.monthly}</span>
                </div>
              </button>
            </div>
            
            {/* One-time Plans */}
            {activeTab === 'one-time' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {oneTimePlans.map((plan, index) => (
                    <motion.div
                      key={`${plan.priceId}-onetime`}
                      className={`relative bg-white dark:bg-gray-800 rounded-xl border ${
                        plan.popular 
                          ? 'border-primary-400 dark:border-primary-500 shadow-lg' 
                          : 'border-gray-200 dark:border-gray-700'
                      } p-6`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                            <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                            {modalText.mostPopular}
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <div className="flex justify-center mb-3">
                          <SafeIcon 
                            icon={plan.icon} 
                            className={`w-10 h-10 ${
                              plan.amount === 5 ? 'text-blue-500' : 
                              plan.amount === 8 ? 'text-purple-500' : 'text-pink-500'
                            }`} 
                          />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {getPlanTitle(plan.amount, plan.type)}
                        </h3>
                        <div className="text-3xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-1">
                          {formatPrice(plan.amount, plan.currency)}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {modalText.oneTimePayment}
                        </p>
                      </div>

                      <div className="space-y-3 mb-6">
                        <p className="text-pink-600 dark:text-pink-400 font-medium text-center">
                          {plan.features[0]}
                        </p>
                        
                        {plan.features.slice(1).map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">{feature}</p>
                          </div>
                        ))}

                        {plan.amount === 5 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '相當於一杯咖啡的價格' : 'About the price of a coffee'}
                            </p>
                          </div>
                        )}
                        
                        {plan.amount === 8 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '支持我們繼續創新' : 'Support our continued innovation'}
                            </p>
                          </div>
                        )}
                        
                        {plan.amount === 10 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-pink-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '您的支持對我們非常重要' : 'Your support means the world to us'}
                            </p>
                          </div>
                        )}
                      </div>

                      <motion.button
                        onClick={() => handlePurchase(plan.paymentLink)}
                        className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          plan.popular
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                        }`}
                        whileTap={{ scale: 0.97 }}
                      >
                        {modalText.choosePlan}
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Monthly Plans */}
            {activeTab === 'subscription' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {subscriptionPlans.map((plan, index) => (
                    <motion.div
                      key={`${plan.priceId}-subscription`}
                      className={`relative bg-white dark:bg-gray-800 rounded-xl border ${
                        plan.popular 
                          ? 'border-primary-400 dark:border-primary-500 shadow-lg' 
                          : 'border-gray-200 dark:border-gray-700'
                      } p-6`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                            <SafeIcon icon={FiZap} className="w-3 h-3 mr-1" />
                            {modalText.bestValue}
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <div className="flex justify-center mb-3">
                          <SafeIcon 
                            icon={plan.icon} 
                            className={`w-10 h-10 ${
                              plan.amount === 5 ? 'text-blue-500' : 
                              plan.amount === 8 ? 'text-purple-500' : 'text-pink-500'
                            }`} 
                          />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {getPlanTitle(plan.amount, plan.type)}
                        </h3>
                        <div className="text-3xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-1">
                          {formatPrice(plan.amount, plan.currency, plan.interval)}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {modalText.recurringMonthly}
                        </p>
                      </div>

                      <div className="space-y-3 mb-6">
                        <p className="text-pink-600 dark:text-pink-400 font-medium text-center">
                          {plan.features[0]}
                        </p>
                        
                        {plan.features.slice(1).map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">{feature}</p>
                          </div>
                        ))}

                        {plan.amount === 5 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '持續支持我們的開發' : 'Ongoing support for our development'}
                            </p>
                          </div>
                        )}
                        
                        {plan.amount === 8 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '幫助我們提供更好的服務' : 'Help us provide better service'}
                            </p>
                          </div>
                        )}
                        
                        {plan.amount === 10 && (
                          <div className="flex items-start">
                            <SafeIcon icon={FiSmile} className="w-5 h-5 text-pink-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {isChinese ? '成為我們的長期贊助者' : 'Become our long-term patron'}
                            </p>
                          </div>
                        )}
                      </div>

                      <motion.button
                        onClick={() => handlePurchase(plan.paymentLink)}
                        className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          plan.popular
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                        }`}
                        whileTap={{ scale: 0.97 }}
                      >
                        {modalText.subscribeNow}
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div className="text-center mt-8 text-xs text-gray-500 dark:text-gray-400">
            {modalText.securePayment}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SupportModal;